import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import testItemApi, { F11A, F11A1, F11N } from '../../../services/testItemApi';

export type Mode = 'browse' | 'add' | 'edit' | 'copy';

export default function useTestItemForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F11A[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const [specs, setSpecs] = useState<F11N[]>([]);

  // Form fields (master)
  const [code, setCode] = useState('');
  const [chineseName, setChineseName] = useState('');
  const [englishName, setEnglishName] = useState('');
  const [outputFormat, setOutputFormat] = useState(1);

  // Form fields (details)
  const [details, setDetails] = useState<F11A1[]>([]);

  const current = records[currentIdx] || null;
  const isEditing = mode !== 'browse';

  // Load all records
  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await testItemApi.list(1, 500);
      setRecords(res.data || []);
    } catch {
      message.error('載入失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load specs
  const loadSpecs = useCallback(async () => {
    try {
      const res = await testItemApi.listSpecs();
      setSpecs(res.data || []);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => { loadRecords(); loadSpecs(); }, [loadRecords, loadSpecs]);

  // Sync form from current record
  const syncFromRecord = useCallback((rec: F11A | null) => {
    if (!rec) {
      setCode(''); setChineseName(''); setEnglishName('');
      setOutputFormat(1); setDetails([]);
      return;
    }
    setCode(rec.f11a_01);
    setChineseName(rec.f11a_11);
    setEnglishName(rec.f11a_14);
    setOutputFormat(rec.f11a_15 || 1);
    setDetails(rec.details || []);
  }, []);

  useEffect(() => { if (mode === 'browse') syncFromRecord(current); }, [currentIdx, records, mode, current, syncFromRecord]);

  // Navigation
  const navFirst = () => { setCurrentIdx(0); };
  const navPrev = () => { setCurrentIdx(i => Math.max(0, i - 1)); };
  const navNext = () => { setCurrentIdx(i => Math.min(records.length - 1, i + 1)); };
  const navLast = () => { setCurrentIdx(records.length - 1); };

  // Quick search
  const quickSearch = useCallback((keyword: string) => {
    const k = keyword.toUpperCase();
    const idx = records.findIndex(r => r.f11a_01.toUpperCase().startsWith(k));
    if (idx >= 0) setCurrentIdx(idx);
    else if (records.length > 0) setCurrentIdx(0);
  }, [records]);

  // Load detail with spec names
  const loadDetail = useCallback(async (code: string) => {
    try {
      const rec = await testItemApi.get(code);
      setDetails(rec.details || []);
    } catch {
      // ignore
    }
  }, []);

  // Actions
  const startAdd = () => {
    setMode('add');
    setCode(''); setChineseName(''); setEnglishName('');
    setOutputFormat(1); setDetails([]);
  };

  const startEdit = () => {
    if (!current) return;
    setMode('edit');
    syncFromRecord(current);
    loadDetail(current.f11a_01);
  };

  const startCopy = () => {
    if (!current) return;
    setMode('copy');
    setCode(''); // new code needed
    setChineseName(current.f11a_11);
    setEnglishName(current.f11a_14);
    setOutputFormat(current.f11a_15 || 1);
    loadDetail(current.f11a_01);
  };

  const cancel = () => {
    setMode('browse');
    syncFromRecord(current);
  };

  const save = async () => {
    try {
      if (mode === 'add') {
        await testItemApi.create(
          { f11a_01: code, f11a_11: chineseName, f11a_14: englishName, f11a_15: outputFormat },
          details.map((d, i) => ({ ...d, f11a1_01: code, f11a1_02: i + 1 }))
        );
        message.success('新增成功');
      } else if (mode === 'edit' && current) {
        await testItemApi.update(current.f11a_01,
          { f11a_01: current.f11a_01, f11a_11: chineseName, f11a_14: englishName, f11a_15: outputFormat },
          details.map((d, i) => ({ ...d, f11a1_01: current.f11a_01, f11a1_02: i + 1 }))
        );
        message.success('修改成功');
      } else if (mode === 'copy' && current) {
        await testItemApi.copy(current.f11a_01, code);
        message.success('複製成功');
      }
      setMode('browse');
      await loadRecords();
      // Position to saved record
      const saved = code || current?.f11a_01 || '';
      const idx = records.findIndex(r => r.f11a_01 === saved);
      if (idx >= 0) setCurrentIdx(idx);
    } catch (err: any) {
      const errors = err?.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        errors.forEach((e: any) => message.error(e.message || e.Message));
      } else {
        message.error(err?.response?.data?.error || '操作失敗');
      }
    }
  };

  const doDelete = async () => {
    if (!current) return;
    try {
      await testItemApi.delete(current.f11a_01);
      message.success('刪除成功');
      await loadRecords();
      if (currentIdx >= records.length - 1) setCurrentIdx(Math.max(0, records.length - 2));
    } catch {
      message.error('刪除失敗');
    }
  };

  // Detail management
  const addDetail = () => {
    setDetails(prev => [...prev, {
      f11a1_01: code || current?.f11a_01 || '',
      f11a1_02: prev.length + 1,
      f11a1_11: '',
      f11a1_12: '',
      f11a1_91: '', f11a1_92: null, f11a1_93: '',
    }]);
  };

  const removeDetail = (idx: number) => {
    setDetails(prev => prev.filter((_, i) => i !== idx));
  };

  const updateDetail = (idx: number, field: keyof F11A1, value: any) => {
    setDetails(prev => prev.map((d, i) => i === idx ? { ...d, [field]: value } : d));
  };

  return {
    mode, records, currentIdx, current, loading, isEditing, specs,
    code, setCode, chineseName, setChineseName, englishName, setEnglishName,
    outputFormat, setOutputFormat, details,
    navFirst, navPrev, navNext, navLast, quickSearch,
    startAdd, startEdit, startCopy, cancel, save, doDelete,
    addDetail, removeDetail, updateDetail,
  };
}
